import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Menu, X, ArrowRight } from 'lucide-react';
import axios from 'axios';
import PersonaDropdown from '../../PersonaDropdown';
import { getCategorySlug, getSubcategorySlug } from '../../utils/slugify';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || '';

const Header = ({ onMenuToggle, isMenuOpen, config }) => {
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [personas, setPersonas] = useState([]);
  
  useEffect(() => {
    fetchCategories();
    fetchPersonas();
    fetchSubcategories();
    fetchPersonas();
  }, []);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchSubcategories = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/subcategories`);
      setSubcategories(response.data);
    } catch (error) {
      console.error('Error fetching subcategories:', error);
    }
  };

  const fetchPersonas = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/techstack`);
      setPersonas(response.data);
    } catch (error) {
      console.error('Error fetching techstack:', error);
      // No fallback - let component handle empty state
      setPersonas([]);
    }
  };

  const getSubcategoriesForCategory = (categoryId) => {
    return subcategories.filter(sub => sub.category_id === categoryId);
  };
  
  return (
    <header className="bg-white shadow-lg sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-2">
              <span className="text-2xl font-bold text-primary-800">{config?.hero?.content?.logo_text || "KLACCELERATORS"}</span>
            </Link>
          </div>
          
          {/* Desktop Navigation */}
          <nav className="hidden md:flex space-x-6 ml-8">
            <Link to="/browse" className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-bold">
              Browse All Accelerators
            </Link>
            
            {/* By Business Solutions Dropdown */}
            <div className="relative group">
              <button className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-bold flex items-center">
                By Business Solutions
                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-[800px] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-4">
                  <div className="grid grid-cols-3 gap-6">
                    {categories.filter(category => category.category_type === 'solution_accelerators').map((category) => {
                      const categorySubcategories = getSubcategoriesForCategory(category.id);
                      return (
                        <div key={category.id} className="space-y-2">
                          {/* Category Link */}
                          <Link
                            to={`/browse?category=${encodeURIComponent(getCategorySlug(category))}`}
                            className="block px-3 py-2 text-sm font-bold text-gray-900 hover:bg-primary-50 hover:text-primary-600 bg-gray-50 rounded-lg border-l-4 border-primary-500"
                          >
                            📁 {category.name}
                          </Link>
                          
                          {/* Subcategories */}
                          {categorySubcategories.length > 0 && (
                            <div className="pl-2 space-y-1">
                              {categorySubcategories.slice(0, 4).map((subcategory) => (
                                <Link
                                  key={subcategory.id}
                                  to={`/browse?category=${encodeURIComponent(getCategorySlug(category))}&subcategory=${encodeURIComponent(getSubcategorySlug(subcategory))}`}
                                  className="block px-3 py-1 text-xs text-gray-600 hover:bg-primary-50 hover:text-primary-600 rounded transition-colors"
                                >
                                  📄 {subcategory.name}
                                </Link>
                              ))}
                              {categorySubcategories.length > 4 && (
                                <Link
                                  to={`/browse?category=${encodeURIComponent(getCategorySlug(category))}`}
                                  className="block px-3 py-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
                                >
                                  + {categorySubcategories.length - 4} more...
                                </Link>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Footer with Browse All link */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <Link
                      to="/browse"
                      className="block text-center px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      Browse All Accelerators →
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* By Engineering Toolkits & Framework Dropdown (Clone of By Business Solutions) */}
            <div className="relative group">
              <button className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-bold flex items-center">
                By Engineering Toolkits & Framework
                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute left-1/2 transform -translate-x-1/2 mt-2 w-[800px] rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-4">
                  <div className="grid grid-cols-3 gap-6">
                    {categories.filter(category => category.category_type === 'engineering_utilities').map((category) => {
                      const categorySubcategories = getSubcategoriesForCategory(category.id);
                      return (
                        <div key={category.id} className="space-y-2">
                          {/* Category Link */}
                          <Link
                            to={`/browse?category=${encodeURIComponent(getCategorySlug(category))}`}
                            className="block px-3 py-2 text-sm font-bold text-gray-900 hover:bg-primary-50 hover:text-primary-600 bg-gray-50 rounded-lg border-l-4 border-primary-500"
                          >
                            ⚙️ {category.name}
                          </Link>
                          
                          {/* Subcategories */}
                          {categorySubcategories.length > 0 && (
                            <div className="pl-2 space-y-1">
                              {categorySubcategories.slice(0, 4).map((subcategory) => (
                                <Link
                                  key={subcategory.id}
                                  to={`/browse?category=${encodeURIComponent(getCategorySlug(category))}&subcategory=${encodeURIComponent(getSubcategorySlug(subcategory))}`}
                                  className="block px-3 py-1 text-xs text-gray-600 hover:bg-primary-50 hover:text-primary-600 rounded transition-colors"
                                >
                                  🔧 {subcategory.name}
                                </Link>
                              ))}
                              {categorySubcategories.length > 4 && (
                                <Link
                                  to={`/browse?category=${encodeURIComponent(getCategorySlug(category))}`}
                                  className="block px-3 py-1 text-xs text-primary-600 hover:text-primary-800 font-medium"
                                >
                                  + {categorySubcategories.length - 4} more...
                                </Link>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  
                  {/* Footer with Browse All link */}
                  <div className="mt-4 pt-3 border-t border-gray-200">
                    <Link
                      to="/browse?category_type=engineering_utilities"
                      className="block text-center px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
                    >
                      Browse All Engineering Tools →
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* By Tech Stack Dropdown */}
            <div className="relative group">
              <button className="text-gray-700 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-bold flex items-center">
                By Tech Stack
                <svg className="ml-1 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <PersonaDropdown personas={personas} />
            </div>

          </nav>

          {/* CTA Button */}
          <div className="hidden md:flex items-center space-x-4">
            <Link
              to="/contact"
              className="bg-primary-600 text-white px-6 py-2 rounded-lg hover:bg-primary-700 transition-colors font-semibold text-sm flex items-center space-x-2 shadow-lg"
            >
              <span>Request for Demo</span>
              <ArrowRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden">
            <button onClick={onMenuToggle} className="text-gray-700 hover:text-primary-600">
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile menu */}
      {isMenuOpen && (
        <div className="md:hidden">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3 bg-white border-t">
            <Link to="/browse" className="block px-3 py-2 rounded-md text-base font-bold text-gray-700 hover:text-primary-600">
              Browse All Accelerators
            </Link>
            
            {/* Mobile Categories */}
            <div className="px-3 py-2">
              <div className="text-sm font-bold text-gray-500 mb-2">By Business Solutions</div>
              {categories.map((category) => (
                <Link
                  key={category.id}
                  to={`/browse?category=${encodeURIComponent(getCategorySlug(category))}`}
                  className="block px-3 py-1 text-sm text-gray-600 hover:text-primary-600"
                >
                  {category.name}
                </Link>
              ))}
            </div>

            {/* Mobile Personas */}
            <div className="px-3 py-2">
              <div className="text-sm font-bold text-gray-500 mb-2">By Tech Stack</div>
              {personas.map((persona) => (
                <Link
                  key={persona.name || persona}
                  to={`/browse?persona=${persona}`}
                  className="block px-3 py-1 text-sm text-gray-600 hover:text-primary-600"
                >
                  {persona.display_name || persona.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Link>
              ))}
            </div>


            
            {/* Mobile CTA Button */}
            <div className="px-3 py-2">
              <Link
                to="/contact"
                className="block bg-primary-600 text-white px-4 py-3 rounded-lg hover:bg-primary-700 transition-colors font-semibold text-center"
              >
                Request for Demo
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;